import React, { useEffect, useMemo, useState } from "react";
import useAccessControl from "../context/useAccessControl";
import {
  getRestaurantProfile,
  saveRestaurantProfile,
} from "../../services/local/restaurantProfile";
import { updateBranchOperationalModules } from "../../services/supabase/companyAdminService";
import { loadWeBranchSettings } from "../../services/supabase/wePedidosService";
import { WE_PEDIDOS_AVAILABLE } from "../../services/local/wePedidosAvailability";
import {
  applyBranchOperationalModuleToggle,
  getBranchOperationalModulesError,
  normalizeBranchOperationalModules,
} from "../../services/local/branchOperationalModules";
import "../../styles/BranchOperationalModules.css";

const buildDraft = (branch, profile, wePedidosEnabled = false) =>
  normalizeBranchOperationalModules(profile || branch || {}, {
    wePedidosEnabled,
  });

const ToggleCard = ({
  title,
  description,
  checked,
  disabled,
  onChange,
  helper,
  accent = "blue",
  comingSoon = false,
}) => (
  <label
    className={`branch-module-card branch-module-card--${accent}${
      disabled ? " branch-module-card--disabled" : ""
    }${comingSoon ? " branch-module-card--coming-soon" : ""}`}
  >
    <span className="branch-module-card__copy">
      <strong>{title}</strong>
      <span>{description}</span>
      {helper ? <small>{helper}</small> : null}
    </span>
    <span className="branch-module-toggle">
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
      />
      <span className="branch-module-toggle__track" aria-hidden="true">
        <span className="branch-module-toggle__thumb" />
      </span>
      <span className="branch-module-toggle__label">
        {comingSoon ? "Proximamente" : checked ? "Activo" : "Pausado"}
      </span>
    </span>
  </label>
);

function BranchOperationalModulesPanel({
  branch,
  profile = null,
  disabled = false,
  onSaved,
  heading = "Integraciones y modulos",
}) {
  const {
    isCreatorMode,
    isSupabaseConfigured,
    refreshWorkspace,
    user,
  } = useAccessControl();
  const resolvedProfile = useMemo(
    () => profile || getRestaurantProfile(branch),
    [branch, profile]
  );
  const [draft, setDraft] = useState(() =>
    buildDraft(branch, resolvedProfile, resolvedProfile?.wePedidosEnabled)
  );
  const [loading, setLoading] = useState(Boolean(branch?.id));
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    const baseDraft = buildDraft(
      branch,
      resolvedProfile,
      resolvedProfile?.wePedidosEnabled
    );

    setDraft(baseDraft);
    setMessage("");
    setError("");

    if (!branch?.id || isCreatorMode || !isSupabaseConfigured) {
      setLoading(false);
      return () => {
        active = false;
      };
    }

    setLoading(true);
    loadWeBranchSettings(branch.id)
      .then((settings) => {
        if (!active) return;
        setDraft(buildDraft(branch, resolvedProfile, settings?.enabled === true));
      })
      .catch((loadError) => {
        if (!active) return;
        setError(
          loadError.message || "No pudimos leer la integracion con We! pedidos."
        );
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [branch, isCreatorMode, isSupabaseConfigured, resolvedProfile]);

  const updateDraft = (field, value) => {
    setError("");
    setMessage("");
    setDraft((current) =>
      applyBranchOperationalModuleToggle(current, field, value)
    );
  };

  const handleSave = async () => {
    setError("");
    setMessage("");

    if (!branch?.id) {
      setError("Selecciona una sucursal antes de guardar sus modulos.");
      return;
    }

    const validationError = getBranchOperationalModulesError(draft);
    if (validationError) {
      setError(validationError);
      return;
    }

    setSaving(true);

    try {
      let savedModules = {
        ...draft,
        operationalSetupCompleted: true,
      };

      if (!isCreatorMode && isSupabaseConfigured) {
        savedModules = await updateBranchOperationalModules(branch.id, draft);
      }

      const nextProfile = saveRestaurantProfile(branch, {
        ...getRestaurantProfile(branch),
        ...savedModules,
        wePedidosEnabled: savedModules.wePedidosEnabled === true,
      });

      setDraft(buildDraft(branch, nextProfile, savedModules.wePedidosEnabled));
      setMessage("Configuracion operativa guardada.");
      onSaved?.(nextProfile, savedModules);

      if (!isCreatorMode && isSupabaseConfigured && user?.id) {
        try {
          await refreshWorkspace(user, branch.id);
        } catch {
          // El guardado ya fue confirmado; el panel se refrescara en el siguiente ingreso.
        }
      }
    } catch (saveError) {
      setError(saveError.message || "No pudimos guardar los modulos de la sucursal.");
    } finally {
      setSaving(false);
    }
  };

  const controlsDisabled = disabled || loading || saving;
  const isKiosk = draft.serviceMode === "kiosk";

  return (
    <section className="branch-modules" aria-busy={loading || saving}>
      <div className="branch-modules__header">
        <div>
          <span className="branch-modules__eyebrow">Por sucursal</span>
          <h3>{heading}</h3>
          <p>
            Elige los canales de venta y las herramientas que usara esta sucursal.
          </p>
        </div>
        <span className="branch-modules__status">
          {draft.operationalSetupCompleted ? "Configurado" : "Primer ingreso"}
        </span>
      </div>

      <div className="branch-modules__group">
        <div className="branch-modules__group-title">Canales de venta</div>
        <div className="branch-modules__grid">
          <ToggleCard
            title="Salon y mesas"
            description="Mesas, QR por mesa, reservas y atencion presencial."
            checked={draft.salonEnabled}
            disabled={controlsDisabled || isKiosk}
            onChange={(value) => updateDraft("salonEnabled", value)}
            helper={isKiosk ? "No aplica a una sucursal configurada como kiosk." : ""}
            accent="orange"
          />
          <ToggleCard
            title="Delivery y retiro"
            description="Pedidos con direccion, retiro en local y seguimiento de entrega."
            checked={draft.deliveryEnabled}
            disabled={controlsDisabled}
            onChange={(value) => updateDraft("deliveryEnabled", value)}
            accent="green"
          />
          <ToggleCard
            title="We! pedidos"
            description="Publica la sucursal en We! pedidos y permite usar la red de riders."
            checked={WE_PEDIDOS_AVAILABLE && draft.wePedidosEnabled}
            disabled={!WE_PEDIDOS_AVAILABLE || controlsDisabled || !draft.deliveryEnabled}
            comingSoon={!WE_PEDIDOS_AVAILABLE}
            onChange={(value) => updateDraft("wePedidosEnabled", value)}
            helper={
              !WE_PEDIDOS_AVAILABLE ? "Todavia no disponible. Conservamos tu configuracion."
              : draft.deliveryEnabled
                ? "Usa la misma carta, precios, stock y pedidos de MozzoApp."
                : "Primero activa Delivery y retiro."
            }
            accent="orange"
          />
        </div>
      </div>

      <div className="branch-modules__group">
        <div className="branch-modules__group-title">Herramientas operativas</div>
        <div className="branch-modules__grid">
          <ToggleCard
            title="Equipo de mozos"
            description="Accesos de mozo, llamados desde la mesa y entrega en salon."
            checked={draft.waiterEnabled}
            disabled={controlsDisabled || !draft.salonEnabled}
            onChange={(value) => updateDraft("waiterEnabled", value)}
            helper={draft.salonEnabled ? "" : "Requiere Salon y mesas."}
          />
          <ToggleCard
            title="Stock e ingredientes"
            description="Valida disponibilidad y descuenta insumos al confirmar pedidos."
            checked={draft.stockEnabled}
            disabled={controlsDisabled}
            onChange={(value) => updateDraft("stockEnabled", value)}
          />
          <ToggleCard
            title="IA / mozo virtual"
            description="Asistencia conversacional y sugerencias dentro de la carta publica."
            checked={draft.aiEnabled}
            disabled={controlsDisabled}
            onChange={(value) => updateDraft("aiEnabled", value)}
          />
        </div>
      </div>

      {(error || message) && (
        <div
          className={`branch-modules__notice ${
            error ? "branch-modules__notice--error" : "branch-modules__notice--success"
          }`}
        >
          {error || message}
        </div>
      )}

      <div className="branch-modules__actions">
        <span>
          {loading
            ? "Cargando integraciones..."
            : "Los cambios se aplican a esta sucursal."}
        </span>
        <button type="button" onClick={handleSave} disabled={controlsDisabled}>
          {saving ? "Guardando..." : "Guardar configuracion"}
        </button>
      </div>
    </section>
  );
}

export default BranchOperationalModulesPanel;
